import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatDate, enrollmentBalance, type Enrollment, type CourseEdition } from "@/lib/api";
import PaymentModal from "@/components/feature/PaymentModal";
import { lazy, Suspense } from "react";

const CertificatePreview = lazy(() => import("@/components/feature/CertificatePreview"));

export default function EstudiantePortalPage() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState<Enrollment | null>(null);
  const [previewCert, setPreviewCert] = useState<Enrollment | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.getEnrollments(), api.getPublicCourses(), api.getPublicDiplomados()])
      .then(([e, courses, diplomados]) => {
        setEnrollments(e);
        const open: CourseEdition[] = [];
        courses.forEach((c) => c.editions?.forEach((ed) => open.push({ ...ed, course: c })));
        diplomados.forEach((d) => d.editions?.forEach((ed) => open.push({
          id: ed.id,
          course_id: d.id,
          edition_label: ed.edition_label,
          start_date: ed.start_date,
          end_date: ed.end_date,
          location: ed.location,
          instructor_id: ed.instructor_id,
          evaluator_id: ed.evaluator_id,
          capacity: ed.capacity,
          price: ed.price,
          status: ed.status,
          course: d as any,
          service_type: "diplomado",
        })));
        setEditions(open);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const enrolledEditionIds = useMemo(() => new Set(enrollments.map((e) => e.edition_id || e.diplomado_edition_id)), [enrollments]);

  const availableEditions = useMemo(
    () => editions.filter((ed) => !enrolledEditionIds.has(ed.id)),
    [editions, enrolledEditionIds]
  );

  const handleQuickEnroll = async (edition: CourseEdition) => {
    if (!user) return;
    const formData = new FormData();
    formData.append("full_name", user.full_name);
    formData.append("email", user.email);
    if (user.phone) formData.append("phone", user.phone);
    if (user.company) formData.append("company", user.company);
    formData.append(edition.service_type === "diplomado" ? "diplomado_edition_id" : "edition_id", edition.id);

    setLoading(true);
    try {
      await api.publicEnrollment(formData);
      load(); // Reloads enrollments and available editions
    } catch (error) {
      alert("Error al inscribirse: " + (error instanceof Error ? error.message : "Desconocido"));
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Mi Portal — {user.full_name}</h1>
        <p className="text-sm text-[var(--color-text-muted)]">{user.company} · {user.position}</p>
      </div>

      {loading ? (
        <div className="text-center py-12"><i className="ri-loader-4-line animate-spin text-2xl"></i></div>
      ) : (
        <>
          <div className="space-y-4">
            <h2 className="font-semibold">Mis inscripciones</h2>
            {enrollments.length === 0 ? (
              <p className="text-[var(--color-text-muted)] text-sm">Sin inscripciones aún.</p>
            ) : (
              enrollments.map((en) => {
                const serviceEdition = en.edition || en.diplomadoEdition;
                const service = en.edition?.course || en.diplomadoEdition?.diplomado;
                const balance = enrollmentBalance(en);
                return (
                <div key={en.id} className="card">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-semibold">{service?.name}</p>
                      <p className="text-xs text-[var(--color-text-muted)]">{serviceEdition?.edition_label} · {formatDate(serviceEdition?.start_date || en.registration_date)}</p>
                      {en.payment_reference && (
                        <p className="text-xs mt-2 font-mono text-[var(--color-accent-oil)]">Ref: {en.payment_reference}</p>
                      )}
                      {!balance.isFullyPaid && (
                        <p className="text-xs text-red-400 mt-1">
                          Saldo pendiente: ${balance.amountRemaining.toLocaleString("en-US")} USD de ${balance.coursePrice.toLocaleString("en-US")} USD
                        </p>
                      )}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded ${en.enrollment_status === "aceptada" ? "badge-success" : "badge-warning"}`}>{en.enrollment_status}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded ${
                          en.payment_status === "pagado" || en.payment_status === "cortesia"
                            ? "badge-success"
                            : "badge-warning"
                        }`}>
                          {en.payment_status === "cortesia" ? "Cortesía / Exonerado" : en.payment_status}
                        </span>
                        <span className="text-[10px] badge-info">{en.attendance_status}</span>
                      </div>
                      
                      {en.payments && en.payments.length > 0 && (
                        <div className="mt-4 border-t border-[var(--color-border)]/50 pt-3">
                          <p className="text-xs font-semibold mb-2">Historial de pagos enviados:</p>
                          <ul className="space-y-2">
                            {en.payments.map((p) => (
                              <li key={p.id} className="text-xs flex items-center justify-between bg-[var(--color-bg-tertiary)] p-2 rounded">
                                <div>
                                  <span className="font-mono text-[var(--color-accent-oil)]">{p.reference || "Sin ref"}</span>
                                  <span className="ml-2 text-[var(--color-text-muted)]">{formatDate(p.payment_date)}</span>
                                  <div className="mt-0.5 font-semibold">${p.amount.toLocaleString("en-US")} USD</div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${
                                    p.status === "pagado" ? "badge-success" : p.status === "rechazado" ? "badge-danger" : "badge-warning"
                                  }`}>{p.status}</span>
                                  {p.proof_url && (
                                    <a href={p.proof_url} target="_blank" rel="noreferrer" className="text-white hover:underline text-[10px]">
                                      Ver comprobante
                                    </a>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-col sm:flex-row items-start sm:items-center">
                      {!balance.isFullyPaid && (
                        <button onClick={() => setPayModal(en)} className="btn-primary text-xs cursor-pointer">
                          {en.payments && en.payments.length > 0 ? "Subir otro comprobante" : "Registrar pago"}
                        </button>
                      )}
                      {en.certificate_issued && en.certificate && (
                        <button onClick={() => setPreviewCert(en)} className="btn-secondary text-xs cursor-pointer">Ver certificado</button>
                      )}
                    </div>
                  </div>
                </div>
              );})
            )}
          </div>

          {availableEditions.length > 0 && (
            <div>
              <h2 className="font-semibold mb-3">Ediciones abiertas</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {availableEditions.map((ed) => (
                  <div key={ed.id} className="card text-sm flex justify-between items-center gap-4">
                    <div>
                      <p className="font-medium">{ed.course?.name}</p>
                      <p className="text-[var(--color-text-muted)]">{ed.edition_label || formatDate(ed.start_date)}</p>
                      <p className="text-[var(--color-accent-oil)] font-bold mt-1">${ed.price.toLocaleString("en-US")}</p>
                    </div>
                    <button onClick={() => handleQuickEnroll(ed)} className="btn-secondary text-xs cursor-pointer shrink-0">
                      Inscribirme
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {payModal && (payModal.edition || payModal.diplomadoEdition) && (
        <PaymentModal
          isOpen
          onClose={() => setPayModal(null)}
          enrollmentId={payModal.id}
          courseName={payModal.edition?.course?.name || payModal.diplomadoEdition?.diplomado?.name || ""}
          editionLabel={payModal.edition?.edition_label || payModal.diplomadoEdition?.edition_label || ""}
          coursePrice={payModal.edition?.price || payModal.diplomadoEdition?.price || 0}
          amountPaid={payModal.payment_amount}
          paymentReference={payModal.payment_reference}
          onSuccess={() => { setPayModal(null); load(); }}
        />
      )}

      {previewCert?.certificate && (
        <Suspense fallback={null}>
          <CertificatePreview
            cert={previewCert.certificate}
            studentName={user.full_name}
            courseName={previewCert.edition?.course?.name || previewCert.diplomadoEdition?.diplomado?.name}
            courseCategory={previewCert.edition?.course?.category || previewCert.diplomadoEdition?.diplomado?.category}
            courseDuration={previewCert.edition?.course?.duration || previewCert.diplomadoEdition?.diplomado?.duration}
            onClose={() => setPreviewCert(null)}
          />
        </Suspense>
      )}
    </div>
  );
}
